import { useEffect, useRef, useState } from "react";
import { ALL_TARGETS_ID, type TargetLocation } from "../lib/locations";
import {
  createLocationId,
  MAX_CUSTOM_LOCATIONS,
  parseCoordinateInput,
  type Coordinate,
  type Settings,
} from "../lib/settings";

interface SettingsSheetProps {
  open: boolean;
  onClose: () => void;
  locations: TargetLocation[];
  settings: Settings;
  onChange: (next: Settings) => void;
  ownPosition: Coordinate | null;
}

function formatCoordinate(coordinate: Coordinate): string {
  return `${coordinate.lat.toFixed(6)}, ${coordinate.lon.toFixed(6)}`;
}

export function SettingsSheet({
  open,
  onClose,
  locations,
  settings,
  onChange,
  ownPosition,
}: SettingsSheetProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [coordDrafts, setCoordDrafts] = useState<Record<string, string>>({});
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [manualDraft, setManualDraft] = useState("");
  const [newName, setNewName] = useState("");
  const [newCoord, setNewCoord] = useState("");

  useEffect(() => {
    if (!open) return;
    setCoordDrafts(Object.fromEntries(locations.map((l) => [l.id, formatCoordinate(l)])));
    setNameDrafts(Object.fromEntries(locations.map((l) => [l.id, l.name])));
    setManualDraft(settings.manualPosition ? formatCoordinate(settings.manualPosition) : "");
    setErrors({});
    dialogRef.current?.focus();
  }, [open, locations, settings.manualPosition]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const setError = (key: string, message: string) =>
    setErrors((prev) => ({ ...prev, [key]: message }));

  const saveLocation = (location: TargetLocation) => {
    const parsed = parseCoordinateInput(coordDrafts[location.id] ?? "");
    if (!parsed) {
      setError(location.id, "Koordinat okunamadı. Örnek: 41.024500, 29.068000");
      return;
    }

    if (location.custom) {
      const name = (nameDrafts[location.id] ?? "").trim();
      if (!name) {
        setError(location.id, "Konum adı boş olamaz.");
        return;
      }
      setError(location.id, "");
      onChange({
        ...settings,
        custom: settings.custom.map((entry) =>
          entry.id === location.id ? { ...entry, name, ...parsed } : entry,
        ),
      });
      return;
    }

    setError(location.id, "");
    onChange({
      ...settings,
      overrides: { ...settings.overrides, [location.id]: parsed },
    });
  };

  const resetLocation = (id: string) => {
    const next = { ...settings.overrides };
    delete next[id];
    onChange({ ...settings, overrides: next });
  };

  const removeLocation = (id: string) => {
    onChange({
      ...settings,
      custom: settings.custom.filter((entry) => entry.id !== id),
      selected: settings.selected === id ? ALL_TARGETS_ID : settings.selected,
    });
  };

  const addLocation = () => {
    const name = newName.trim();
    if (!name) {
      setError("__new", "Konum adı girin.");
      return;
    }
    const parsed = parseCoordinateInput(newCoord);
    if (!parsed) {
      setError("__new", "Koordinat okunamadı. Örnek: 41.024500, 29.068000");
      return;
    }
    setError("__new", "");
    onChange({
      ...settings,
      custom: [...settings.custom, { id: createLocationId(), name, ...parsed }],
    });
    setNewName("");
    setNewCoord("");
  };

  const applyManual = () => {
    if (!manualDraft.trim()) {
      onChange({ ...settings, manualPosition: null });
      return;
    }
    const parsed = parseCoordinateInput(manualDraft);
    if (!parsed) {
      setError("__manual", "Koordinat okunamadı.");
      return;
    }
    setError("__manual", "");
    onChange({ ...settings, manualPosition: parsed });
  };

  const canAdd = settings.custom.length < MAX_CUSTOM_LOCATIONS;

  return (
    <div className="sheet" role="presentation" onClick={onClose}>
      <div
        className="sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        tabIndex={-1}
        ref={dialogRef}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="sheet__head">
          <h2 id="sheet-title">Ayarlar</h2>
          <button type="button" className="sheet__close" onClick={onClose}>
            Kapat
          </button>
        </header>

        <section className="sheet__section">
          <h3>Konumlar</h3>
          <p className="sheet__hint">
            Enlem ve boylamı yapıştırın. Google Haritalar bağlantısındaki
            <code> @41.0245,29.0680 </code> biçimi de kabul edilir. Kâbe
            koordinatı sabittir.
          </p>
          {locations.map((location) => (
            <div key={location.id} className="field">
              {location.custom ? (
                <label htmlFor={`name-${location.id}`}>
                  Konum adı
                  <input
                    id={`name-${location.id}`}
                    type="text"
                    className="field__input"
                    value={nameDrafts[location.id] ?? ""}
                    onChange={(event) =>
                      setNameDrafts((prev) => ({
                        ...prev,
                        [location.id]: event.target.value,
                      }))
                    }
                  />
                </label>
              ) : (
                <label htmlFor={`coord-${location.id}`}>
                  {location.name}
                  <span className="field__sub">{location.subtitle}</span>
                </label>
              )}
              <div className="field__row">
                <input
                  id={`coord-${location.id}`}
                  type="text"
                  aria-label={`${location.name} koordinatı`}
                  value={coordDrafts[location.id] ?? ""}
                  disabled={location.fixed}
                  onChange={(event) =>
                    setCoordDrafts((prev) => ({
                      ...prev,
                      [location.id]: event.target.value,
                    }))
                  }
                />
                {!location.fixed && (
                  <button type="button" onClick={() => saveLocation(location)}>
                    Kaydet
                  </button>
                )}
              </div>
              <div className="field__actions">
                {!location.fixed &&
                  !location.custom &&
                  settings.overrides[location.id] && (
                    <button
                      type="button"
                      className="link"
                      onClick={() => resetLocation(location.id)}
                    >
                      Varsayılana dön
                    </button>
                  )}
                {location.custom && (
                  <button
                    type="button"
                    className="link link--danger"
                    onClick={() => removeLocation(location.id)}
                  >
                    Konumu sil
                  </button>
                )}
              </div>
              {errors[location.id] && <p className="field__error">{errors[location.id]}</p>}
            </div>
          ))}
        </section>

        <section className="sheet__section">
          <h3>Konum ekle</h3>
          {canAdd ? (
            <div className="field">
              <label htmlFor="new-name">
                Konum adı
                <input
                  id="new-name"
                  type="text"
                  className="field__input"
                  placeholder="Örn. Ev"
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                />
              </label>
              <div className="field__row">
                <input
                  id="new-coord"
                  type="text"
                  aria-label="Yeni konumun koordinatı"
                  placeholder="41.024500, 29.068000"
                  value={newCoord}
                  onChange={(event) => setNewCoord(event.target.value)}
                />
                <button type="button" onClick={addLocation}>
                  Ekle
                </button>
              </div>
              {errors.__new && <p className="field__error">{errors.__new}</p>}
            </div>
          ) : (
            <p className="sheet__hint">
              En fazla {MAX_CUSTOM_LOCATIONS} konum eklenebilir. Yeni bir tane
              eklemek için önce birini silin.
            </p>
          )}
        </section>

        <section className="sheet__section">
          <h3>Kendi konumum</h3>
          <p className="sheet__hint">
            Konum izni yoksa ya da kapalı alandaysanız konumunuzu elle
            girebilirsiniz. Boş bırakılırsa cihazın GPS verisi kullanılır.
          </p>
          <div className="field">
            <label htmlFor="manual-position">Enlem, boylam</label>
            <div className="field__row">
              <input
                id="manual-position"
                type="text"
                placeholder={ownPosition ? formatCoordinate(ownPosition) : "41.008200, 28.978400"}
                value={manualDraft}
                onChange={(event) => setManualDraft(event.target.value)}
              />
              <button type="button" onClick={applyManual}>
                Kaydet
              </button>
            </div>
            {errors.__manual && <p className="field__error">{errors.__manual}</p>}
          </div>
        </section>

        <section className="sheet__section">
          <h3>Manyetik sapma düzeltmesi</h3>
          <p className="sheet__hint">
            Android cihazların çoğu manyetik kuzeyi bildirir. Gerçek kuzeye
            çevirmek için bulunduğunuz yerin sapma değerini girin
            (İstanbul için yaklaşık +6°, Mekke için +4°). iPhone'da gerek yoktur.
          </p>
          <div className="field">
            <label htmlFor="declination">Sapma (derece, doğuya pozitif)</label>
            <div className="field__row">
              <input
                id="declination"
                type="number"
                inputMode="decimal"
                step="0.5"
                min="-45"
                max="45"
                value={settings.declination}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  onChange({
                    ...settings,
                    declination: Number.isFinite(value)
                      ? Math.max(-45, Math.min(45, value))
                      : 0,
                  });
                }}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
